import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { Loader2Icon, MessageCircleIcon, UserXIcon } from "lucide-react";
import { Applicant } from "@type/applicant";
import { Job } from "@type/job";
import { rejectApplicantViaWhatsapp } from "@service/hiring-flow";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";

interface Props {
    isOpen: boolean;
    applicant: Applicant | null;
    /** Bulk selection — when provided, rejects every candidate via WhatsApp. Falls
     *  back to the single `applicant` when omitted/empty. */
    applicants?: Applicant[];
    job?: Job | null;
    onClose: () => void;
    /** Called after a successful WhatsApp reject so the parent reloads the board. */
    onRejected: () => void;
}

/**
 * Friendly copy for the template-send failures (BE ourteam-backend#3877 / PR #3878).
 * The template works outside the 24h window, so there is no "window closed" case.
 * Prefer the BE-provided `reason`/message when present.
 */
function messageForError(status?: number, reason?: string): string {
    if (reason) return `${reason} The candidate was NOT rejected.`;
    switch (status) {
        case 404:
            return "This candidate hasn't opted in to WhatsApp, so we can't message them. The candidate was NOT rejected.";
        case 409:
            return "WhatsApp reject isn't available for this candidate — WhatsApp may be disabled for your company, or a rejection was already sent. The candidate was NOT rejected.";
        case 502:
            return "Couldn't send the WhatsApp rejection template. The candidate was NOT rejected — please try again in a moment.";
        default:
            return "Couldn't send the WhatsApp rejection. The candidate was NOT rejected — please try again.";
    }
}

/**
 * "Reject via WhatsApp" dialog (#3186, BE ourteam-backend#3877 / PR #3878). Used
 * for flagged hiring-flow candidates who have NO email address — the standard
 * email reject can't reach them, so HR rejects them and the BE notifies them over
 * WhatsApp using the APPROVED `rejection` template (fixed, non-editable). HR only
 * confirms + optionally adds a private internal note. On success the BE atomically
 * sets the application to Rejected AND delivers the template.
 */
export default function RejectCandidateWhatsappDialog({ isOpen, applicant, applicants, job, onClose, onRejected }: Props) {
    const snackbar = useSnackbar();
    const [internalNote, setInternalNote] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Target candidates — a bulk selection when provided, else the single applicant.
    const targets = (applicants && applicants.length > 0 ? applicants : (applicant ? [applicant] : []))
        .filter((a) => a?.applicant_id);
    const isBulk = targets.length > 1;

    useEffect(() => {
        if (!isOpen) return;
        setInternalNote("");
        setIsLoading(false);
    }, [isOpen, applicant?.id, targets.length]);

    const handleSubmit = async () => {
        if (targets.length === 0 || !job?.id) return;
        setIsLoading(true);
        const note = internalNote.trim() || undefined;
        try {
            const results = await Promise.allSettled(
                targets.map((a) => rejectApplicantViaWhatsapp(job.id!, a.applicant_id, { internal_note: note })),
            );
            const ok = results.filter((r) => r.status === "fulfilled").length;
            const failed = results.length - ok;
            if (ok > 0) {
                snackbar.showSuccess(
                    isBulk
                        ? `${ok} candidate${ok > 1 ? "s" : ""} rejected over WhatsApp${failed ? ` · ${failed} failed` : ""}.`
                        : "Candidate rejected — the message was sent over WhatsApp.",
                );
                onRejected();
                onClose();
            } else {
                const first = results.find((r) => r.status === "rejected") as PromiseRejectedResult | undefined;
                const resp = (first?.reason as { response?: { status?: number; data?: { reason?: string; message?: string } } })?.response;
                snackbar.showError(messageForError(resp?.status, resp?.data?.reason || resp?.data?.message));
            }
        } catch (error) {
            catchErrorLog(`Failed to reject candidate via WhatsApp: ${error}`);
            snackbar.showError(messageForError());
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isLoading) onClose(); }}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600">
                            <UserXIcon className="h-4 w-4" />
                        </span>
                        Reject via WhatsApp
                    </DialogTitle>
                    <DialogDescription>
                        {isBulk
                            ? <><span className="font-medium text-gray-700 dark:text-gray-200">{targets.length} candidates</span> will each be notified</>
                            : <>{targets[0]?.applicant_name ? <span className="font-medium text-gray-700 dark:text-gray-200">{targets[0].applicant_name}</span> : "This candidate"} will be notified</>}
                        {" "}over WhatsApp using ourteam's approved rejection template and moved to <span className="font-medium">Closed</span>.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="wa-reject-note" className="text-sm font-medium">Internal rejection note <span className="font-normal text-gray-400">(optional)</span></Label>
                        <Textarea
                            id="wa-reject-note"
                            value={internalNote}
                            onChange={(e) => setInternalNote(e.target.value)}
                            rows={2}
                            placeholder="Private note for your team — not sent to the candidate."
                            disabled={isLoading}
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={onClose} disabled={isLoading}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="gap-2 bg-red-600 text-white hover:bg-red-700"
                    >
                        {isLoading ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <MessageCircleIcon className="h-4 w-4" />}
                        {isLoading ? "Sending…" : "Reject & send"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
